// OWNER: AREA-OPS (applications/*). Components go in src/features/attachments.
// Attachments: limits, allowed types, retention, and the attachment sources fetch rules use.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { applicationQuery } from '@/api/queries/applications'
import { usePermissions } from '@/auth/usePermissions'
import { ErrorState } from '@/components/QueryState'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { SkeletonText } from '@/components/ui/skeleton'
import { AttachmentSettingsCard } from '@/features/attachments/AttachmentSettingsCard'
import { SourcesPanel } from '@/features/attachments/SourcesPanel'
import { UnsavedChangesScope } from '@/features/ops/UnsavedChangesScope'

export const Route = createFileRoute('/applications/$appId/attachments')({
  staticData: { crumb: 'Attachments' },
  component: AppAttachmentsPage,
})

function AppAttachmentsPage() {
  const { appId } = Route.useParams()
  const app = useQuery(applicationQuery(appId))
  const { can } = usePermissions()

  if (app.isError) return <ErrorState error={app.error} onRetry={() => void app.refetch()} />
  if (!app.data)
    return (
      <Card>
        <CardBody className="pt-4">
          <SkeletonText lines={8} />
        </CardBody>
      </Card>
    )

  return (
    <UnsavedChangesScope>
      <div className="space-y-5">
        {!can('manage_config') && (
          <p className="rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
            You can view these settings. Only Admins can change them.
          </p>
        )}
        <AttachmentSettingsCard app={app.data} />
        <Card id="sources" className="scroll-mt-6">
          <CardHeader
            title="Attachment sources"
            description={
              <>
                Systems MailSmith may download files from. See{' '}
                <Link
                  to="/help/$slug"
                  params={{ slug: 'attachment-sources' }}
                  className="text-brass hover:underline"
                >
                  Fetch files from your systems
                </Link>
                .
              </>
            }
          />
          <CardBody>
            <SourcesPanel appId={appId} />
          </CardBody>
        </Card>
        <p className="text-sm text-ink-2">
          Reusable files such as logos and terms live in{' '}
          <Link
            to="/applications/$appId/assets"
            params={{ appId }}
            className="text-brass hover:underline"
          >
            this application’s assets
          </Link>
          .
        </p>
      </div>
    </UnsavedChangesScope>
  )
}
